import { useRef } from 'react';
import * as PIXI from 'pixi.js';
import { PixiApplicationManager } from '../../core/PixiApplication';
import { ViewportManager } from '../../core/ViewportManager';
import { GridRenderer } from '../../core/GridRenderer';
import { InteractionManager } from '../../core/InteractionManager';
import { BundleManager } from '../../managers/BundleManager';
import { Connector } from '../../entities/Connector';
import { HarnessNode } from '../../entities/HarnessNode';
import { WireBundle } from '../../entities/WireBundle';
import { WirePreview } from '../../entities/WirePreview';
import { BundlePreview } from '../../entities/BundlePreview';
import { Tooltip } from '../../entities/Tooltip';
import { PreviewNode } from '../../entities/PreviewNode';
import { BundleDragState, PixiAppRefs } from './types';
import { GroupDragState } from './groupDrag';

/**
 * Creates the full set of PIXI-layer refs once and returns a stable container.
 * The container identity never changes across renders, so modules can safely
 * close over it.
 */
export function usePixiAppRefs(): PixiAppRefs {
  const appManager = useRef<PixiApplicationManager | null>(null);
  const viewportManager = useRef<ViewportManager | null>(null);
  const gridRenderer = useRef<GridRenderer | null>(null);
  const interactionManager = useRef<InteractionManager | null>(null);
  const bundleManager = useRef<BundleManager | null>(null);
  const mounted = useRef(false);
  const cleanupFunction = useRef<(() => void) | null>(null);
  const tickerCallbackAttached = useRef(false);
  const canvasContainer = useRef<HTMLDivElement | null>(null);

  const connectorEntities = useRef<Map<string, Connector>>(new Map());
  const nodeEntities = useRef<Map<string, HarnessNode>>(new Map());
  const wireBundleEntities = useRef<Map<string, WireBundle>>(new Map());
  const viewportContainer = useRef<PIXI.Container | null>(null);
  const backgroundSprite = useRef<PIXI.Sprite | null>(null);
  const backgroundSrc = useRef<string | null>(null);
  const stateUnsubscribe = useRef<(() => void) | null>(null);
  const interactionUnsubscribe = useRef<(() => void) | null>(null);

  const wirePreview = useRef<WirePreview | null>(null);
  const temporaryNodeIds = useRef<string[]>([]);
  const tooltip = useRef<Tooltip | null>(null);
  const previewNode = useRef<PreviewNode | null>(null);
  const bundlePreview = useRef<BundlePreview | null>(null);
  const bundleDragState = useRef<BundleDragState | null>(null);
  const selectionBox = useRef<PIXI.Graphics | null>(null);
  const groupDrag = useRef<GroupDragState | null>(null);

  const refs = useRef<PixiAppRefs | null>(null);
  if (!refs.current) {
    refs.current = {
      appManager,
      viewportManager,
      gridRenderer,
      interactionManager,
      bundleManager,
      mounted,
      cleanupFunction,
      tickerCallbackAttached,
      canvasContainer,
      connectorEntities,
      nodeEntities,
      wireBundleEntities,
      viewportContainer,
      backgroundSprite,
      backgroundSrc,
      stateUnsubscribe,
      interactionUnsubscribe,
      wirePreview,
      temporaryNodeIds,
      tooltip,
      previewNode,
      bundlePreview,
      bundleDragState,
      selectionBox,
      groupDrag
    };
  }

  return refs.current;
}
